import SixDigitCodeInput from "@via/components/code-input/six-digit-code-input.tsx";
import Modal from "@via/components/modal.tsx";
import { LockedNotice } from "@via/pages/authentication/mfa/mfa-notices.tsx";
import {
  mfaErrorMessage,
  useAdminResetMfa,
} from "@via/pages/authentication/mfa/mfa.api.tsx";
import {
  type AdminMfaResetResponse,
  MAX_RESET_REASON_LENGTH,
  type User,
} from "common";
import { type JSX, useState } from "react";

export const MFA_RESET_PROCEDURE =
  "Before you continue, verify this person over a channel that does not depend on their account: a video call against a known identity, or a call back to contact details on record. Never send the one time code to the account's own email address, since that inbox may be what was compromised.";

const getUserIdentity = (user: User | null) => {
  if (!user) return "this user";
  return user.email || user.userName || user.name || `user id ${user.id}`;
};

type ResetMfaModalProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  user: User | null;
  onClosed: () => void;
};

export default function ResetMfaModal({
  open,
  setOpen,
  user,
  onClosed,
}: ResetMfaModalProps): JSX.Element {
  const reset = useAdminResetMfa();
  const [reason, setReason] = useState("");
  const [attempt, setAttempt] = useState(0);
  const [result, setResult] = useState<AdminMfaResetResponse | null>(null);

  const errorInfo = reset.isError ? mfaErrorMessage(reset.error) : null;
  const isLocked = Boolean(
    errorInfo?.code === "locked" && errorInfo.lockedUntil
  );

  const close = (): void => {
    setOpen(false);
    setReason("");
    setResult(null);
    setAttempt(0);
    reset.reset();
    onClosed();
  };

  return (
    <Modal
      open={open}
      setOpen={next => (next ? setOpen(true) : close())}
      title="Reset multi-factor sign-in"
      showButtons={false}>
      <div className="w-[460px] max-w-[calc(100vw-2rem)] space-y-4">
        {result ? (
          <>
            <p className="text-sm text-gray-700">
              Give this one time code to{" "}
              <strong>{getUserIdentity(user)}</strong>. It is shown only now and
              cannot be shown again. It expires{" "}
              {new Date(result.handoffExpiresAt).toLocaleString()}.
            </p>
            <p
              data-testid="mfa-handoff-code"
              className="rounded-md bg-gray-50 p-3 text-center font-mono text-xl">
              {result.handoffCode}
            </p>
            <p className="text-sm text-gray-700">
              {result.notified
                ? "The user was emailed that their multi-factor sign-in was reset. The code was not included."
                : "The notification email could not be sent. Tell the user another way that their multi-factor sign-in was reset."}
            </p>
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                onClick={() =>
                  void navigator.clipboard?.writeText(result.handoffCode)
                }>
                Copy
              </button>
              <button
                type="button"
                className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500"
                onClick={close}>
                Done
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="text-sm text-gray-700">
              Reset multi-factor sign-in for{" "}
              <strong>{getUserIdentity(user)}</strong>. Their authenticator and
              recovery codes stop working and every session ends.
            </p>
            <p className="rounded-md bg-amber-50 p-3 text-sm text-amber-900">
              {MFA_RESET_PROCEDURE}
            </p>
            <div>
              <label
                htmlFor="mfa-reset-reason"
                className="block text-sm font-medium text-gray-900">
                Reason
              </label>
              <textarea
                id="mfa-reset-reason"
                maxLength={MAX_RESET_REASON_LENGTH}
                value={reason}
                onChange={e => setReason(e.target.value)}
                className="mt-1 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600"
              />
            </div>
            <p className="text-sm text-gray-600">
              Enter a code from your own authenticator app to confirm.
            </p>
            <SixDigitCodeInput
              autoFocus={false}
              resetKey={attempt}
              disabled={reset.isPending || !reason.trim() || !user || isLocked}
              onComplete={code => {
                if (!user) return;
                reset.mutate(
                  { userId: user.id, body: { reason: reason.trim(), code } },
                  {
                    onSuccess: setResult,
                    onError: () => setAttempt(a => a + 1),
                  }
                );
              }}
            />
            {isLocked ? (
              <LockedNotice until={errorInfo!.lockedUntil!} />
            ) : (
              errorInfo && (
                <p role="alert" className="text-sm font-semibold text-rose-600">
                  {errorInfo.message}
                </p>
              )
            )}
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                onClick={close}>
                Cancel
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
